/**
 * Decimal-backed numeric input that handles in-flight editing correctly.
 *
 * Why this exists: the naive pattern
 *
 *   <input
 *     type="number"
 *     value={d.toFixed(2)}
 *     onChange={e => setD(new Decimal(e.target.value))}
 *   />
 *
 * looks fine but breaks editing in two ways:
 *   1. Backspacing past the last digit produces e.target.value === ''.
 *      Skipping the update keeps the parent at the old value, which then
 *      reformats and snaps the field back  -  empty state never sticks, so
 *      you can never clear the field to start over.
 *   2. Every keystroke triggers parent re-render, parent reformats via
 *      toFixed(), and the input's value attribute snaps to that formatted
 *      string. Cursor jumps; intermediate states like "1." (typing 1.5) get
 *      reformatted to "1.00" mid-keystroke.
 *
 * Fix: keep a local string state that mirrors what the user typed verbatim.
 * Push to parent only when the string parses cleanly as a Decimal. Re-sync
 * from props.value only when the parent value actually changed (e.g. live
 * data update from elsewhere)  -  detected by comparing against the last
 * formatted value we sent up.
 */

import { useState, useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';
import { Decimal } from '../engine/money.js';

export interface DecimalInputProps {
  /**
   * Current value. Use undefined to represent "no value"  -  only meaningful
   * when paired with onClear. Without onClear, parent won't ever see undefined,
   * so it's safe to type as Decimal there.
   */
  value: Decimal | undefined;
  onChange: (d: Decimal) => void;
  /**
   * If provided, the input supports being fully cleared (backspace to empty)
   * and fires this callback. Use for inputs where "no value" is a meaningful
   * state  -  e.g. ETF cost basis (some lots are unknown), loan APR (manual
   * entries don't have one). Without this, empty strings stay in local state
   * but the parent never sees them.
   */
  onClear?: () => void;
  /** Number of decimals to display when not actively editing. Default 2. */
  decimals?: number;
  /** HTML step attribute. Default "0.01". */
  step?: string;
  /** Optional placeholder shown when local string is empty. */
  placeholder?: string;
  className?: string;
  style?: CSSProperties;
  title?: string;
  disabled?: boolean;
}

export function DecimalInput(props: DecimalInputProps): JSX.Element {
  const decimals = props.decimals ?? 2;
  const formatValue = (v: Decimal | undefined): string =>
    v === undefined ? '' : v.toFixed(decimals);
  const [local, setLocal] = useState<string>(() => formatValue(props.value));
  const lastPropValueRef = useRef<string>(formatValue(props.value));

  useEffect(() => {
    const formatted = formatValue(props.value);
    if (formatted !== lastPropValueRef.current) {
      setLocal(formatted);
      lastPropValueRef.current = formatted;
    }
    // formatValue is stable (only depends on decimals which is in deps)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value, decimals]);

  return (
    <input
      type="number"
      step={props.step ?? '0.01'}
      value={local}
      placeholder={props.placeholder}
      className={props.className}
      style={props.style}
      title={props.title}
      disabled={props.disabled}
      onChange={e => {
        const v = e.target.value;
        setLocal(v);
        if (v === '') {
          if (props.onClear) {
            lastPropValueRef.current = '';
            props.onClear();
          }
          return;
        }
        if (v === '-' || v === '.') return;
        try {
          const d = new Decimal(v);
          if (d.isFinite()) {
            lastPropValueRef.current = d.toFixed(decimals);
            props.onChange(d);
          }
        } catch {
          /* hold locally  -  invalid intermediate string */
        }
      }}
      onBlur={() => {
        // If cleared and onClear is supported, leave the field empty so the
        // placeholder shows. Otherwise snap back to canonical format.
        if (local === '' && props.onClear) return;
        const fmt = formatValue(props.value);
        setLocal(fmt);
        lastPropValueRef.current = fmt;
      }}
    />
  );
}
